
import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from './GlobalContext';
import { UserGroupIcon } from "../../icons/heroIcons";
export default function SportSelection(props) {

  const { isH2H, setIsH2H } = useContext(GlobalContext);
  const sportsKey = Object.values(props.sports).join('|');
  const liveSports = new Set(
    (Array.isArray(props.liveSports) ? props.liveSports : [])
      .map((league) => String(league || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const sliderShellRef = useRef(null);
  const hasCenteredInitialSportRef = useRef(false);
  const strokeAnimationTimeoutRef = useRef(null);
  const prefetchIntentTimeoutRef = useRef(null);
  const [animatingStrokeLeague, setAnimatingStrokeLeague] = useState(null);
  const dragStateRef = useRef({
    isDown: false,
    moved: false,
    startX: 0,
    scrollLeft: 0,
    suppressClick: false,
  });

  const sportIcons = {
    'topplays': 'fa-solid fa-trophy',
    'nba': 'fa-solid fa-basketball',
    'nba2h': 'fa-solid fa-basketball',
    'march': 'fa-solid fa-basketball',
    'cbb': 'fa-solid fa-basketball',
    'cbb2h': 'fa-solid fa-basketball',
    'wnba': 'fa-solid fa-basketball',
    'wnba1h': 'fa-solid fa-basketball',
    'wcbb': 'fa-solid fa-basketball',
    '3pt': 'fa-solid fa-basketball',
    'nfl': 'fa-solid fa-football',
    'nflp': 'fa-solid fa-football',
    'nflszn': 'fa-solid fa-football',
    'mlb': 'fa-solid fa-baseball',
    'kbo': 'fa-solid fa-baseball',
    'wbc': 'fa-solid fa-baseball',
    'nhl': 'fa-solid fa-hockey-puck',
    'ufc': 'fa-solid fa-hand-fist',
    'cfb': 'fa-solid fa-football',
    'pga': 'fa-solid fa-golf-ball-tee',
    'soccer': 'fa-solid fa-futbol',
    'worldcup': 'fa-solid fa-futbol',
    'worldcup1h': 'fa-solid fa-futbol',
    'worldcup2h': 'fa-solid fa-futbol',
    'cs2': 'fa-solid fa-crosshairs',
    'lol': 'fa-solid fa-dragon',
    'val': 'fa-solid fa-shield',
    'cod': 'fa-solid fa-crosshairs',
    'dota2': 'fa-solid fa-gamepad',
    'r6': 'fa-solid fa-shield',
    'tennis': 'fa-solid fa-table-tennis',
    'f1': 'fa-solid fa-flag-checkered',
    'darts': 'fa-solid fa-bullseye',
    'boxing': 'fa-solid fa-hand-fist',
    'allstar': 'fa-solid fa-medal',
    'allstarmlb': 'fa-solid fa-baseball',
    'hrderby': 'fa-solid fa-baseball',
    'nascar': 'fa-solid fa-flag-checkered',
    'nbasl': 'fa-solid fa-basketball',
    'fpa': 'fa-solid fa-basketball',
    'fiba': 'fa-solid fa-basketball',
    'celeb': 'fa-solid fa-basketball',
    'unr': 'fa-solid fa-basketball',
    'hotdog': 'fa-solid fa-hotdog',
    'cfl': 'fa-solid fa-football',
    'cricket': 'fa-solid fa-baseball',
    'tt': 'fa-solid fa-table-tennis',
    'celebgolf': 'fa-solid fa-golf',
    'tbt': 'fa-solid fa-basketball',
    'olympics': 'fa-solid fa-medal',
    'ohockey': 'fa-solid fa-hockey',
    'bowling': 'fa-solid fa-bowling-ball'
  };

  const sportLabels = {
    allstarmlb: 'MLB ALL-STAR',
    hrderby: 'HOME RUN DERBY',
    topplays: 'TOP PICKS',
    wnba1h: 'WNBA 1H',
    worldcup: 'WORLD CUP',
    worldcup1h: 'WORLD CUP 1H',
    worldcup2h: 'WORLD CUP 2H',
  };

  useEffect(() => () => {
    if (strokeAnimationTimeoutRef.current) {
      window.clearTimeout(strokeAnimationTimeoutRef.current);
    }
    if (prefetchIntentTimeoutRef.current) {
      window.clearTimeout(prefetchIntentTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const resetRetainedInteractionState = () => {
      if (document.hidden) {
        return;
      }

      if (prefetchIntentTimeoutRef.current) {
        window.clearTimeout(prefetchIntentTimeoutRef.current);
        prefetchIntentTimeoutRef.current = null;
      }

      dragStateRef.current = {
        isDown: false,
        moved: false,
        startX: 0,
        scrollLeft: sliderShellRef.current?.scrollLeft || 0,
        suppressClick: false,
      };
      sliderShellRef.current?.classList.remove('dragging');
    };

    document.addEventListener('visibilitychange', resetRetainedInteractionState);
    window.addEventListener('pageshow', resetRetainedInteractionState);
    window.addEventListener('ww:median-app-resumed', resetRetainedInteractionState);

    return () => {
      document.removeEventListener('visibilitychange', resetRetainedInteractionState);
      window.removeEventListener('pageshow', resetRetainedInteractionState);
      window.removeEventListener('ww:median-app-resumed', resetRetainedInteractionState);
    };
  }, []);

  useEffect(() => {
    const sliderList = sliderShellRef.current;
    if (!sliderList) {
      return undefined;
    }

    const selectedButton = sliderList.querySelector('.sport-btn.selected');
    if (!selectedButton) {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      const sliderRect = sliderList.getBoundingClientRect();
      const selectedRect = selectedButton.getBoundingClientRect();
      const targetLeft = sliderList.scrollLeft
        + selectedRect.left
        - sliderRect.left
        - ((sliderRect.width - selectedRect.width) / 2);
      const maxLeft = sliderList.scrollWidth - sliderList.clientWidth;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = hasCenteredInitialSportRef.current && !prefersReducedMotion ? 'smooth' : 'auto';

      sliderList.scrollTo({
        left: Math.max(0, Math.min(targetLeft, maxLeft)),
        behavior,
      });
      hasCenteredInitialSportRef.current = true;
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [props.sport, sportsKey]);

  const handleToggleH2h = () => {

    const urlParams = new URLSearchParams(window.location.search);
    const opponentOid = urlParams.get('h2h');
    if (opponentOid) {
      $('#h2h-match-modal').modal('show');
      return;
    }

    setIsH2H(!isH2H);
    if (!isH2H) {
      $('#h2h-modal').modal('show');
    }
  };

  const getSliderList = () => sliderShellRef.current;

  const stopDrag = () => {
    const sliderList = getSliderList();
    const dragState = dragStateRef.current;

    dragState.isDown = false;
    sliderList?.classList.remove('dragging');

    if (dragState.moved) {
      dragState.suppressClick = true;
      window.setTimeout(() => {
        dragStateRef.current.suppressClick = false;
        dragStateRef.current.moved = false;
      }, 0);
    }
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) {
      return;
    }

    const sliderList = getSliderList();
    if (!sliderList) {
      return;
    }

    dragStateRef.current.isDown = true;
    dragStateRef.current.moved = false;
    dragStateRef.current.startX = e.pageX;
    dragStateRef.current.scrollLeft = sliderList.scrollLeft;
    sliderList.classList.add('dragging');
  };

  const handleMouseMove = (e) => {
    const dragState = dragStateRef.current;
    if (!dragState.isDown) {
      return;
    }

    const sliderList = getSliderList();
    if (!sliderList) {
      return;
    }

    const walk = (e.pageX - dragState.startX) * 1.5;
    if (Math.abs(walk) > 4) {
      dragState.moved = true;
      e.preventDefault();
    }

    sliderList.scrollLeft = dragState.scrollLeft - walk;
  };

  const handleClickCapture = (e) => {
    if (!dragStateRef.current.suppressClick) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  };

  const handleSportClick = (e, league, shouldUseGradientStroke) => {
    if (prefetchIntentTimeoutRef.current) {
      window.clearTimeout(prefetchIntentTimeoutRef.current);
      prefetchIntentTimeoutRef.current = null;
    }
    props.onClick(e);

    if (!shouldUseGradientStroke) {
      return;
    }

    if (strokeAnimationTimeoutRef.current) {
      window.clearTimeout(strokeAnimationTimeoutRef.current);
    }

    setAnimatingStrokeLeague(null);
    window.requestAnimationFrame(() => {
      setAnimatingStrokeLeague(league);
      strokeAnimationTimeoutRef.current = window.setTimeout(() => {
        setAnimatingStrokeLeague(null);
        strokeAnimationTimeoutRef.current = null;
      }, 20000);
    });
  };

  const handleSportIntent = (league) => {
    if (league === props.sport || typeof props.onPrefetch !== 'function') {
      return;
    }

    if (prefetchIntentTimeoutRef.current) {
      window.clearTimeout(prefetchIntentTimeoutRef.current);
    }

    // A pointerenter is commonly dispatched immediately before a click,
    // especially in touch WebViews. Delay speculative work so it does not
    // race the foreground request for the same league.
    prefetchIntentTimeoutRef.current = window.setTimeout(() => {
      prefetchIntentTimeoutRef.current = null;
      props.onPrefetch(league);
    }, 750);
  };

  const cancelSportIntent = () => {
    if (!prefetchIntentTimeoutRef.current) {
      return;
    }

    window.clearTimeout(prefetchIntentTimeoutRef.current);
    prefetchIntentTimeoutRef.current = null;
  };

  return (
    <div
      className="sport-selection-slider"
      role="list"
      ref={sliderShellRef}
      onClickCapture={handleClickCapture}
      onMouseDown={handleMouseDown}
      onMouseLeave={stopDrag}
      onMouseMove={handleMouseMove}
      onMouseUp={stopDrag}
    >
      { isH2H && document.body.classList.contains('logged-in') && document.getElementById('verified').value == '1' &&
        <button onClick={handleToggleH2h} data-prop='h2h' className={'sport-btn select-btn' + (isH2H ? ' selected' : '')} value={isH2H}>
          <div><UserGroupIcon aria-hidden="true" className="ww-inline-icon" /></div>
          H2H
        </button>
      }

      {Object.keys(props.sports).map((sport, index) => {
        const league = props.sports[sport];
        const iconClassName = sportIcons[league] || 'fa-solid fa-trophy';
        const isLive = liveSports.has(String(league || '').trim().toLowerCase());
        const label = sportLabels[league] || league.toUpperCase();
        const shouldUseGradientStroke = index < 10;
        const className = [
          'sport-btn select-btn',
          props.sport === league ? 'selected' : '',
          shouldUseGradientStroke ? 'sport-btn--gradient-stroke' : '',
          animatingStrokeLeague === league ? 'sport-btn--stroke-animating' : '',
          'sport-btn--ready',
        ].filter(Boolean).join(' ');

        return (
          <button
            key={league}
            onClick={(e) => handleSportClick(e, league, shouldUseGradientStroke)}
            onBlur={cancelSportIntent}
            onFocus={() => handleSportIntent(league)}
            onPointerEnter={() => handleSportIntent(league)}
            onPointerLeave={cancelSportIntent}
            data-prop='sport'
            className={className}
            value={league}
            aria-label={isLive ? `${label}, live event available` : undefined}
          >
            <span className="sport-btn__icon-wrap">
              <i aria-hidden="true" className={`${iconClassName} ww-inline-icon sport-btn__icon`}></i>
              {isLive && <span aria-hidden="true" className="sport-btn__live-indicator" />}
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );
}
